import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { companiesApi } from "@/services/api";
import {
  Plus,
  Search,
  MoreHorizontal,
  Building2,
  Loader2,
  X,
  Pencil,
  Trash2,
  RotateCcw,
  ExternalLink,
  Copy,
  CheckCircle,
  Settings,
} from "lucide-react";
import { formatDate } from "@/lib/utils";

interface WalletInfo {
  id: string;
  address: string;
  networkId: string;
  networkName?: string;
}

interface Company {
  id: string;
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  email: string | null;
  telefone: string | null;
  endereco: string | null;
  cidade: string | null;
  estado: string | null;
  cep: string | null;
  contatoNome: string | null;
  status: "PENDING" | "ACTIVE" | "SUSPENDED" | "INACTIVE";
  createdAt: string;
  wallet?: WalletInfo | null;
}

interface CompanyForm {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  email: string;
  telefone: string;
  endereco: string;
  cidade: string;
  estado: string;
  cep: string;
  contatoNome: string;
}

const initialForm: CompanyForm = {
  cnpj: "",
  razaoSocial: "",
  nomeFantasia: "",
  email: "",
  telefone: "",
  endereco: "",
  cidade: "",
  estado: "",
  cep: "",
  contatoNome: "",
};

const statusConfig = {
  PENDING: { label: "Pendente", variant: "warning" as const },
  ACTIVE: { label: "Ativo", variant: "success" as const },
  SUSPENDED: { label: "Suspenso", variant: "destructive" as const },
  INACTIVE: { label: "Inativo", variant: "secondary" as const },
};

const UFS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO",
  "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI",
  "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

const explorerUrls: Record<string, string> = {
  XDC_MAINNET: "https://xdcscan.io/address/",
  XDC_TESTNET: "https://apothem.xdcscan.io/address/",
};

export function CompaniesPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  // Form states
  const [createForm, setCreateForm] = useState<CompanyForm>(initialForm);
  const [editForm, setEditForm] = useState<CompanyForm>(initialForm);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [formError, setFormError] = useState("");

  // Confirm dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    confirmText: string;
    variant: "default" | "danger";
    onConfirm: () => void;
    loading: boolean;
    error: string;
  }>({
    open: false,
    title: "",
    description: "",
    confirmText: "Confirmar",
    variant: "default",
    onConfirm: () => {},
    loading: false,
    error: "",
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  async function loadCompanies() {
    try {
      const data = await companiesApi.list(true); // includeWallet=true
      setCompanies(data);
    } catch (error) {
      console.error("Erro ao carregar empresas:", error);
    } finally {
      setLoading(false);
    }
  }

  function truncateAddress(address: string) {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  function copyAddress(address: string) {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  }

  function getExplorerUrl(address: string, networkId: string) {
    const baseUrl = explorerUrls[networkId];
    if (!baseUrl) return null;
    return `${baseUrl}${address}`;
  }

  async function handleCreate() {
    setFormError("");

    // Validations
    if (!createForm.cnpj || !createForm.razaoSocial) {
      setFormError("CNPJ e Razão Social são obrigatórios");
      return;
    }

    // Remove formatting from CNPJ
    const cnpjClean = createForm.cnpj.replace(/\D/g, "");
    if (cnpjClean.length !== 14) {
      setFormError("CNPJ inválido");
      return;
    }

    setCreating(true);
    try {
      const payload = {
        ...createForm,
        cnpj: cnpjClean,
      };

      await companiesApi.create(payload);
      setCreateForm(initialForm);
      setShowCreateModal(false);
      await loadCompanies();
    } catch (error: any) {
      setFormError(error.message || "Erro ao criar empresa");
    } finally {
      setCreating(false);
    }
  }

  async function handleUpdate() {
    if (!selectedCompany) return;
    setFormError("");

    if (!editForm.razaoSocial) {
      setFormError("Razão Social é obrigatória");
      return;
    }

    setUpdating(true);
    try {
      // Não enviar cnpj no update (campo não editável)
      const { cnpj, ...updateData } = editForm;
      await companiesApi.update(selectedCompany.id, updateData);
      setShowEditModal(false);
      setSelectedCompany(null);
      await loadCompanies();
    } catch (error: any) {
      setFormError(error.message || "Erro ao atualizar empresa");
    } finally {
      setUpdating(false);
    }
  }

  function openConfirmDialog(
    title: string,
    description: string,
    confirmText: string,
    variant: "default" | "danger",
    onConfirm: () => Promise<void>
  ) {
    setConfirmDialog({
      open: true,
      title,
      description,
      confirmText,
      variant,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true, error: "" }));
        try {
          await onConfirm();
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false, error: "" }));
        } catch (error: any) {
          const errorMessage = error?.message || "Erro ao executar operação";
          setConfirmDialog((prev) => ({ ...prev, loading: false, error: errorMessage }));
        }
      },
      loading: false,
      error: "",
    });
  }

  function closeConfirmDialog() {
    setConfirmDialog((prev) => ({ ...prev, open: false, error: "" }));
  }

  function handleApprove(id: string) {
    openConfirmDialog(
      "Aprovar Empresa",
      "Tem certeza que deseja aprovar esta empresa? Ela terá acesso ao sistema.",
      "Aprovar",
      "default",
      async () => {
        await companiesApi.approve(id);
        await loadCompanies();
      }
    );
  }

  function handleSuspend(id: string) {
    openConfirmDialog(
      "Suspender Empresa",
      "Tem certeza que deseja suspender esta empresa? Ela perderá acesso ao sistema temporariamente.",
      "Suspender",
      "danger",
      async () => {
        await companiesApi.suspend(id);
        await loadCompanies();
      }
    );
  }

  function handleReactivate(id: string) {
    openConfirmDialog(
      "Reativar Empresa",
      "Tem certeza que deseja reativar esta empresa? Ela voltará a ter acesso ao sistema.",
      "Reativar",
      "default",
      async () => {
        await companiesApi.reactivate(id);
        await loadCompanies();
      }
    );
  }

  function handleDelete(id: string) {
    openConfirmDialog(
      "Excluir Empresa",
      "Tem certeza que deseja excluir esta empresa? A empresa será desativada e não aparecerá mais na listagem.",
      "Excluir",
      "danger",
      async () => {
        await companiesApi.delete(id);
        await loadCompanies();
      }
    );
  }

  function openEditModal(company: Company) {
    setSelectedCompany(company);
    setEditForm({
      cnpj: company.cnpj,
      razaoSocial: company.razaoSocial,
      nomeFantasia: company.nomeFantasia || "",
      email: company.email || "",
      telefone: company.telefone || "",
      endereco: company.endereco || "",
      cidade: company.cidade || "",
      estado: company.estado || "",
      cep: company.cep || "",
      contatoNome: company.contatoNome || "",
    });
    setFormError("");
    setShowEditModal(true);
  }

  const filteredCompanies = companies.filter(
    (c) =>
      c.razaoSocial.toLowerCase().includes(search.toLowerCase()) ||
      c.cnpj.includes(search) ||
      c.nomeFantasia?.toLowerCase().includes(search.toLowerCase())
  );

  // Calculate stats
  const stats = useMemo(() => {
    const total = companies.length;
    const active = companies.filter((c) => c.status === "ACTIVE").length;
    const pending = companies.filter((c) => c.status === "PENDING").length;
    const suspended = companies.filter((c) => c.status === "SUSPENDED").length;
    return { total, active, pending, suspended };
  }, [companies]);

  const formatCnpj = (cnpj: string) => {
    return cnpj.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5"
    );
  };

  const formatCnpjInput = (value: string) => {
    const numbers = value.replace(/\D/g, "").slice(0, 14);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    if (numbers.length <= 8)
      return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
    if (numbers.length <= 12)
      return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12)}`;
  };

  const formatTelefoneInput = (value: string) => {
    const numbers = value.replace(/\D/g, "").slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 10)
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  };

  const formatCepInput = (value: string) => {
    const numbers = value.replace(/\D/g, "").slice(0, 8);
    if (numbers.length <= 5) return numbers;
    return `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Building2 className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Empresas"
        description="Gerenciamento de empresas clientes"
        stats={[
          { value: stats.total, label: "Total" },
          { value: stats.active, label: "Ativas" },
          { value: stats.pending, label: "Pendentes" },
          { value: stats.suspended, label: "Suspensas" },
        ]}
        actions={
          <Button
            onClick={() => {
              setCreateForm(initialForm);
              setFormError("");
              setShowCreateModal(true);
            }}
            className="bg-white text-blue-600 hover:bg-blue-50"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Empresa
          </Button>
        }
      />

      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome, CNPJ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Companies Table */}
      <Card>
        <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Carregando...
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma empresa encontrada</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium">Empresa</th>
                    <th className="p-4 font-medium">CNPJ</th>
                    <th className="p-4 font-medium">Carteira</th>
                    <th className="p-4 font-medium">Localização</th>
                    <th className="p-4 font-medium">Status</th>
                    <th className="p-4 font-medium">Criado em</th>
                    <th className="p-4 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompanies.map((company) => (
                    <tr
                      key={company.id}
                      className="border-b border-border last:border-0 hover:bg-muted/50"
                    >
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{company.razaoSocial}</p>
                          {company.nomeFantasia && (
                            <p className="text-sm text-muted-foreground">
                              {company.nomeFantasia}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-sm">
                        {formatCnpj(company.cnpj)}
                      </td>
                      <td className="p-4">
                        {company.wallet ? (
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                              {truncateAddress(company.wallet.address)}
                            </code>
                            {getExplorerUrl(company.wallet.address, company.wallet.networkId) && (
                              <a
                                href={getExplorerUrl(company.wallet.address, company.wallet.networkId)!}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800"
                                title="Ver no Explorer"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            )}
                            <button
                              onClick={() => copyAddress(company.wallet!.address)}
                              className="text-muted-foreground hover:text-foreground"
                              title="Copiar endereco"
                            >
                              {copiedAddress === company.wallet.address ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {company.cidade && company.estado
                          ? `${company.cidade}/${company.estado}`
                          : "-"}
                      </td>
                      <td className="p-4">
                        <Badge variant={statusConfig[company.status].variant}>
                          {statusConfig[company.status].label}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {formatDate(company.createdAt)}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          {company.status === "PENDING" && (
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => handleApprove(company.id)}
                            >
                              Aprovar
                            </Button>
                          )}
                          {company.status === "ACTIVE" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSuspend(company.id)}
                            >
                              Suspender
                            </Button>
                          )}
                          {company.status === "SUSPENDED" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReactivate(company.id)}
                            >
                              <RotateCcw className="w-3 h-3 mr-1" />
                              Reativar
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[120px]">
                              <DropdownMenuItem onClick={() => openEditModal(company)}>
                                <Pencil className="w-3 h-3 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/admin/companies/${company.id}/features`)}>
                                <Settings className="w-3 h-3 mr-2" />
                                Features
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(company.id)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="w-3 h-3 mr-2" />
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Nova Empresa</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCreateModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {formError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ *</Label>
                  <Input
                    id="cnpj"
                    placeholder="00.000.000/0000-00"
                    value={createForm.cnpj}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        cnpj: formatCnpjInput(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="razaoSocial">Razão Social *</Label>
                  <Input
                    id="razaoSocial"
                    placeholder="Nome da empresa"
                    value={createForm.razaoSocial}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, razaoSocial: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="nomeFantasia">Nome Fantasia</Label>
                <Input
                  id="nomeFantasia"
                  placeholder="Nome fantasia (opcional)"
                  value={createForm.nomeFantasia}
                  onChange={(e) =>
                    setCreateForm({
                      ...createForm,
                      nomeFantasia: e.target.value,
                    })
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="contato@empresa.com"
                    value={createForm.email}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, email: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone</Label>
                  <Input
                    id="telefone"
                    placeholder="(00) 00000-0000"
                    value={createForm.telefone}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, telefone: formatTelefoneInput(e.target.value) })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="endereco">Endereço</Label>
                <Input
                  id="endereco"
                  placeholder="Rua, número, complemento"
                  value={createForm.endereco}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, endereco: e.target.value })
                  }
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cidade">Cidade</Label>
                  <Input
                    id="cidade"
                    placeholder="Cidade"
                    value={createForm.cidade}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, cidade: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estado">Estado</Label>
                  <select
                    id="estado"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={createForm.estado}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, estado: e.target.value })
                    }
                  >
                    <option value="">Selecione</option>
                    {UFS.map((uf) => (
                      <option key={uf} value={uf}>
                        {uf}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cep">CEP</Label>
                  <Input
                    id="cep"
                    placeholder="00000-000"
                    value={createForm.cep}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, cep: formatCepInput(e.target.value) })
                    }
                  />
                </div>
              </div>

              {/* Nome do Responsável */}
              <div className="space-y-2">
                <Label htmlFor="contatoNome">Nome do Responsável</Label>
                <Input
                  id="contatoNome"
                  placeholder="Nome do responsável pela empresa"
                  value={createForm.contatoNome}
                  onChange={(e) =>
                    setCreateForm({ ...createForm, contatoNome: e.target.value })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Ao aprovar a empresa, um usuário será criado com este nome e o email corporativo informado acima.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancelar
                </Button>
                <Button onClick={handleCreate} disabled={creating}>
                  {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Criar Empresa
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Editar Empresa</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEditModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {formError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-cnpj">CNPJ</Label>
                  <Input
                    id="edit-cnpj"
                    value={formatCnpj(selectedCompany.cnpj)}
                    disabled
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-razaoSocial">Razão Social *</Label>
                  <Input
                    id="edit-razaoSocial"
                    placeholder="Nome da empresa"
                    value={editForm.razaoSocial}
                    onChange={(e) =>
                      setEditForm({ ...editForm, razaoSocial: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-nomeFantasia">Nome Fantasia</Label>
                <Input
                  id="edit-nomeFantasia"
                  placeholder="Nome fantasia (opcional)"
                  value={editForm.nomeFantasia}
                  onChange={(e) =>
                    setEditForm({ ...editForm, nomeFantasia: e.target.value })
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    placeholder="contato@empresa.com"
                    value={editForm.email}
                    onChange={(e) =>
                      setEditForm({ ...editForm, email: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-telefone">Telefone</Label>
                  <Input
                    id="edit-telefone"
                    placeholder="(00) 00000-0000"
                    value={editForm.telefone}
                    onChange={(e) =>
                      setEditForm({ ...editForm, telefone: formatTelefoneInput(e.target.value) })
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-endereco">Endereço</Label>
                <Input
                  id="edit-endereco"
                  placeholder="Rua, número, complemento"
                  value={editForm.endereco}
                  onChange={(e) =>
                    setEditForm({ ...editForm, endereco: e.target.value })
                  }
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-cidade">Cidade</Label>
                  <Input
                    id="edit-cidade"
                    placeholder="Cidade"
                    value={editForm.cidade}
                    onChange={(e) =>
                      setEditForm({ ...editForm, cidade: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-estado">Estado</Label>
                  <select
                    id="edit-estado"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={editForm.estado}
                    onChange={(e) =>
                      setEditForm({ ...editForm, estado: e.target.value })
                    }
                  >
                    <option value="">Selecione</option>
                    {UFS.map((uf) => (
                      <option key={uf} value={uf}>
                        {uf}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-cep">CEP</Label>
                  <Input
                    id="edit-cep"
                    placeholder="00000-000"
                    value={editForm.cep}
                    onChange={(e) =>
                      setEditForm({ ...editForm, cep: formatCepInput(e.target.value) })
                    }
                  />
                </div>
              </div>

              {/* Nome do Responsável */}
              <div className="space-y-2">
                <Label htmlFor="edit-contatoNome">Nome do Responsável</Label>
                <Input
                  id="edit-contatoNome"
                  placeholder="Nome do responsável pela empresa"
                  value={editForm.contatoNome}
                  onChange={(e) =>
                    setEditForm({ ...editForm, contatoNome: e.target.value })
                  }
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancelar
                </Button>
                <Button onClick={handleUpdate} disabled={updating}>
                  {updating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Salvar Alterações
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
        loading={confirmDialog.loading}
        error={confirmDialog.error}
      />
    </div>
  );
}
